import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowClockwise16Regular,
  ArrowUpload20Regular,
  Building16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Delete16Regular,
  DocumentEdit16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { StatusPill } from '../../../theme/components/StatusPill';
import { StatusTone } from '../../../theme/guideTokens';
import { ProviderFormComponent, ProviderFormValues } from '../ProviderForm/ProviderFormComponent';
import { formatFileSize } from '../../../functions/AzureFileUpload.Functions';
import { formatImportAmount, formatImportDate } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  formatTaxId,
  INBOX_ACCEPT,
  isLikelyTooLarge,
  isRowBusy,
  safeImportRoute,
  unresolvedCount,
} from '../../../functions/ProviderInbox.Functions';
import { InboxRow, InboxRowStatus, ProviderInboxIdentifyResult, ProviderInboxResolveRequest, ProviderInboxResolveResult } from '../../../models/Entities/ProviderInbox/ProviderInbox';
import { useInboxQueue } from './useInboxQueue';
import './ProviderFromInvoice.Styles.css';

export interface ProviderFromInvoiceDrawerProps {
  open: boolean;
  /** Archivos a agregar (cada lote con un id nuevo: soltados o elegidos en la lista). */
  batch?: { id: number; files: File[] };
  /** Se cerró (ya se descartó lo pendiente). */
  onDismiss: () => void;
  identify: (file: File) => Promise<ProviderInboxIdentifyResult>;
  resolve: (inboxId: string, body: ProviderInboxResolveRequest) => Promise<ProviderInboxResolveResult>;
  discard: (inboxId: string) => Promise<void>;
  /** Una fila quedó resuelta: refrescar la lista de proveedores. */
  onResolved?: () => void;
  /** "Ir al proveedor" (ruta interna validada). */
  onGoToProvider: (route: string) => void;
}

/** Pastilla de cada estado: texto + símbolo (nunca sólo color). */
const STATUS_PILLS: Record<InboxRowStatus, { tone: StatusTone; symbol: string }> = {
  invalid: { tone: 'danger', symbol: '×' },
  queued: { tone: 'neutral', symbol: '○' },
  reading: { tone: 'info', symbol: '…' },
  resolving: { tone: 'info', symbol: '…' },
  needsInput: { tone: 'warning', symbol: '!' },
  rejected: { tone: 'danger', symbol: '×' },
  resolvedExisting: { tone: 'success', symbol: '✓' },
  resolvedCreated: { tone: 'success', symbol: '●' },
  error: { tone: 'danger', symbol: '!' },
  discarded: { tone: 'neutral', symbol: '–' },
};

/**
 * "Alta desde factura" (spec 009, US4, FR-021): una fila por archivo con su estado ("Leyendo…", "Proveedor creado: X",
 * "Proveedor existente: X, archivo agregado", "Faltan datos", "No se puede: la factura la emitió la institución",
 * "Error, reintentar"), los datos detectados desplegables, los avisos de la lectura y las acciones de la fila. Al cerrar
 * con filas sin resolver pide confirmación y las descarta (DELETE de la bandeja).
 */
export const ProviderFromInvoiceDrawer = (props: ProviderFromInvoiceDrawerProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerList.fromInvoice.${key}`, options) as string;
  const queue = useInboxQueue({
    identify: props.identify,
    resolve: props.resolve,
    discard: props.discard,
    onResolved: () => props.onResolved?.(),
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [completing, setCompleting] = useState<InboxRow | undefined>(undefined);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const lastBatch = useRef<number | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);

  const { addFiles } = queue;
  useEffect(() => {
    if (!props.open || !props.batch || props.batch.id === lastBatch.current) return;
    lastBatch.current = props.batch.id;
    addFiles(props.batch.files);
  }, [props.open, props.batch, addFiles]);

  const pending = unresolvedCount(queue.rows);
  const done = queue.rows.filter((row) => row.status === 'resolvedCreated' || row.status === 'resolvedExisting').length;
  const total = queue.rows.filter((row) => row.status !== 'invalid' && row.status !== 'discarded').length;

  const close = async () => {
    setClosing(true);
    await queue.closeAll();
    setClosing(false);
    setConfirmClose(false);
    setExpanded(new Set());
    props.onDismiss();
  };

  const requestClose = () => {
    if (closing) return;
    if (pending > 0) setConfirmClose(true);
    else close();
  };

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const statusText = (row: InboxRow): string => {
    if (row.status === 'resolvedCreated' || row.status === 'resolvedExisting') {
      const name = row.resolved?.businessName?.trim() || tr('provider', { id: row.resolved?.providerId });
      if (row.resolved?.alreadyResolved) return tr('status.resolvedAlready', { name });
      return tr(`status.${row.status}`, { name });
    }
    return tr(`status.${row.status}`);
  };

  const errorText = (row: InboxRow): string | undefined => {
    if (!row.error) return undefined;
    if (row.error.stage === 'identify' && isLikelyTooLarge(row.error.code, row.file.size)) return tr('tooLarge');
    const details = row.error.errors.filter(Boolean);
    return details.length > 0 ? Array.from(new Set(details)).join(' ') : row.error.message;
  };

  const actionLabel = (action: string, row: InboxRow) => tr('actions.label', { action: tr(`actions.${action}`), file: row.file.name });

  const renderDetails = (row: InboxRow) => {
    const voucher = row.identify?.voucher;
    if (!voucher) return <p className="pfi-muted">{tr('details.noVoucher')}</p>;
    const issuer = voucher.issuer;
    const entry = (label: string, value: React.ReactNode) =>
      value !== undefined && value !== null && value !== '' ? (
        <div className="pfi-details__entry">
          <dt>{tr(`details.${label}`)}</dt>
          <dd>{value}</dd>
        </div>
      ) : null;
    const taxIdValue = issuer?.taxId ? (
      <>
        {formatTaxId(issuer.taxId)}{' '}
        {row.identify?.taxIdValid === true && (
          <StatusPill tone="success" symbol="✓" className="pfi-pill">
            {tr('details.taxIdValid')}
          </StatusPill>
        )}
        {row.identify?.taxIdValid === false && (
          <StatusPill tone="danger" symbol="×" className="pfi-pill">
            {tr('details.taxIdInvalid')}
          </StatusPill>
        )}
      </>
    ) : undefined;
    const number =
      voucher.pointOfSale !== undefined && voucher.pointOfSale !== null && voucher.number !== undefined && voucher.number !== null
        ? `${String(voucher.pointOfSale).padStart(5, '0')}-${String(voucher.number).padStart(8, '0')}`
        : undefined;
    const period =
      voucher.periodFrom || voucher.periodTo
        ? tr('details.periodValue', { from: formatImportDate(voucher.periodFrom), to: formatImportDate(voucher.periodTo) })
        : undefined;
    return (
      <div className="pfi-details" data-testid={`pfi-details-${row.key}`}>
        <section>
          <h4>{tr('details.issuer')}</h4>
          <dl>
            {entry('businessName', issuer?.businessName)}
            {entry('taxId', taxIdValue)}
            {entry('address', issuer?.address)}
            {entry('vatCondition', issuer?.vatCondition)}
            {entry('grossIncome', issuer?.grossIncomeNumber)}
            {entry('activityStart', issuer?.activityStartDate ? formatImportDate(issuer.activityStartDate) : undefined)}
          </dl>
        </section>
        <section>
          <h4>{tr('details.voucher')}</h4>
          <dl>
            {entry('type', voucher.voucherType || voucher.voucherTypeCode)}
            {entry('number', number)}
            {entry('issueDate', voucher.issueDate ? formatImportDate(voucher.issueDate) : undefined)}
            {entry('period', period)}
            {entry('paymentDue', voucher.paymentDueDate ? formatImportDate(voucher.paymentDueDate) : undefined)}
            {entry('cae', voucher.cae)}
            {entry('total', voucher.total !== undefined && voucher.total !== null ? formatImportAmount(voucher.total, voucher.currencyCode) : undefined)}
          </dl>
        </section>
      </div>
    );
  };

  const renderRow = (row: InboxRow) => {
    const pill = STATUS_PILLS[row.status];
    const busy = isRowBusy(row);
    const open = expanded.has(row.key);
    const warnings = (row.identify?.warnings ?? []).filter(Boolean);
    const error = errorText(row);
    const resolved = row.status === 'resolvedCreated' || row.status === 'resolvedExisting';
    const detailsId = `pfi-details-region-${row.key}`;
    return (
      <li key={row.key} className={`pfi-row pfi-row--${row.status}`} data-testid={`pfi-row-${row.key}`} data-status={row.status}>
        <div className="pfi-row__head">
          <span className="pfi-row__file">
            <span className="pfi-row__name">{row.file.name}</span>
            <span className="pfi-muted">{formatFileSize(row.file.size)}</span>
          </span>
          <span className="pfi-row__status" aria-live="polite">
            {busy && <Spinner size="extra-tiny" aria-hidden="true" />}
            <StatusPill tone={pill.tone} symbol={pill.symbol} className="pfi-pill pfi-pill--wrap" data-testid={`pfi-status-${row.key}`}>
              {statusText(row)}
            </StatusPill>
          </span>
        </div>
        {row.status === 'invalid' && row.invalidReason && <p className="pfi-row__text">{tr(`invalid.${row.invalidReason}`)}</p>}
        {row.status === 'needsInput' && !row.error && <p className="pfi-row__text">{tr('needsInputText')}</p>}
        {row.status === 'rejected' && <p className="pfi-row__text">{tr('rejectedText')}</p>}
        {error && (
          <GuideMessageBar intent="error" layout="multiline" data-testid={`pfi-error-${row.key}`}>
            <MessageBarBody>
              <MessageBarTitle>{row.error?.stage === 'identify' ? tr('identifyError') : tr('resolveError')}</MessageBarTitle>
              {error}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {warnings.length > 0 && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid={`pfi-warnings-${row.key}`}>
            <MessageBarBody>
              <MessageBarTitle>{tr('warningsTitle')}</MessageBarTitle>
              <ul className="pfi-list">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </MessageBarBody>
          </GuideMessageBar>
        )}
        <div className="pfi-row__actions">
          {row.identify && (
            <Button
              size="small"
              appearance="subtle"
              icon={open ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
              aria-expanded={open}
              aria-controls={detailsId}
              onClick={() => toggle(row.key)}
              data-testid={`pfi-toggle-${row.key}`}
            >
              {open ? tr('actions.hideDetails') : tr('actions.showDetails')}
            </Button>
          )}
          {resolved && row.resolved && (
            <Button
              size="small"
              appearance="primary"
              icon={<Building16Regular />}
              aria-label={actionLabel('goToProvider', row)}
              onClick={() => props.onGoToProvider(safeImportRoute(row.resolved!))}
              data-testid={`pfi-go-${row.key}`}
            >
              {tr('actions.goToProvider')}
            </Button>
          )}
          {row.status === 'needsInput' && (
            <Button
              size="small"
              appearance="primary"
              icon={<DocumentEdit16Regular />}
              aria-label={actionLabel('complete', row)}
              onClick={() => setCompleting(row)}
              data-testid={`pfi-complete-${row.key}`}
            >
              {tr('actions.complete')}
            </Button>
          )}
          {row.status === 'error' && (
            <Button
              size="small"
              appearance="secondary"
              icon={<ArrowClockwise16Regular />}
              aria-label={actionLabel('retry', row)}
              onClick={() => queue.retry(row.key)}
              data-testid={`pfi-retry-${row.key}`}
            >
              {tr('actions.retry')}
            </Button>
          )}
          {(row.status === 'needsInput' || row.status === 'rejected' || row.status === 'error' || row.status === 'invalid') && (
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete16Regular />}
              aria-label={actionLabel('discard', row)}
              onClick={() => queue.discardRow(row.key)}
              data-testid={`pfi-discard-${row.key}`}
            >
              {tr('actions.discard')}
            </Button>
          )}
        </div>
        {row.identify && open && (
          <div id={detailsId} role="region" aria-label={tr('actions.showDetails')}>
            {renderDetails(row)}
          </div>
        )}
      </li>
    );
  };

  const issuer = completing?.identify?.voucher?.issuer;

  return (
    <>
      <GuideDrawer
        open={props.open}
        size="medium"
        onDismiss={requestClose}
        preventDismiss={closing}
        closeLabel={tr('close')}
        eyebrow={tr('eyebrow')}
        title={tr('title')}
        subtitle={tr('subtitle')}
        testId="pfi-drawer"
        secondaryAction={{ label: tr('close'), onClick: requestClose, disabled: closing }}
        headerActions={
          <>
            <Button appearance="subtle" icon={<ArrowUpload20Regular />} onClick={() => input.current?.click()} data-testid="pfi-add">
              {tr('addFiles')}
            </Button>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              accept={INBOX_ACCEPT}
              data-testid="pfi-input"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                if (files.length > 0) queue.addFiles(files);
              }}
            />
          </>
        }
      >
        {total > 0 && (
          <p className="pfi-muted" aria-live="polite" data-testid="pfi-summary">
            {tr('summary', { done, total })}
          </p>
        )}
        <ul className="pfi-rows" aria-label={tr('listLabel')} data-testid="pfi-rows">
          {queue.rows.map(renderRow)}
        </ul>
      </GuideDrawer>

      <ProviderFormComponent
        isOpen={!!completing}
        openPanel={() => undefined}
        dismissPanel={() => setCompleting(undefined)}
        initialValues={{ businessName: issuer?.businessName ?? '', taxId: issuer?.taxId ?? '' }}
        showTaxId
        eyebrow={completing ? tr('form.eyebrow', { file: completing.file.name }) : undefined}
        subtitle={tr('form.subtitle')}
        onSubmit={(values: ProviderFormValues) => (completing ? queue.complete(completing.key, values) : Promise.resolve())}
      />

      <Dialog open={confirmClose} modalType="alert" onOpenChange={(_event, data) => !data.open && !closing && setConfirmClose(false)}>
        <DialogSurface data-testid="pfi-confirm-close">
          <DialogBody>
            <DialogTitle>{tr('confirmClose.title')}</DialogTitle>
            <DialogContent>{tr('confirmClose.text', { count: pending })}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmClose(false)} disabled={closing} data-testid="pfi-confirm-cancel">
                {tr('confirmClose.cancel')}
              </Button>
              <Button appearance="primary" onClick={close} disabled={closing} aria-busy={closing || undefined} data-testid="pfi-confirm-discard">
                {closing ? tr('confirmClose.discarding') : tr('confirmClose.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ProviderFromInvoiceDrawer;
